import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { pb } from '../lib/pb';
import { errorMessage } from '../lib/errors';
import { toast } from '../store/store';
import { getData, setData, useData } from './dataStore';
import { DataIcon } from './DataIcon';
import type { DataRecord } from './types';

/**
 * A new asset is a copy of an existing one (the template): Unity copies the template asset, so meshes,
 * icons and every hidden field come along, and only what the designer changes differs. The server
 * (pb_hooks/lib/data-edit.js prepareRecordCreate) validates the name, path, key and values.
 */
export function NewRecordDialog({ category, onClose }: { category: string; onClose: () => void }) {
  const { t } = useTranslation();
  const records = useData((s) => s.records);
  const cat = useData((s) => s.categories.find((c) => c.key === category));
  const templates = useMemo(
    () =>
      Object.values(records)
        .filter((r) => r.category === category && r.guid && r.syncState !== 'missing_in_unity')
        .sort((a, b) => a.name.localeCompare(b.name, 'sv')),
    [records, category],
  );
  const [templateId, setTemplateId] = useState(templates[0]?.id ?? '');
  const template = records[templateId] as DataRecord | undefined;
  const [key, setKey] = useState('');
  const [assetName, setAssetName] = useState('');
  const [folder, setFolder] = useState('');
  const [busy, setBusy] = useState(false);

  const keyField = cat?.keyField || cat?.nameField || '';
  const templateFolder = template?.assetPath
    ? template.assetPath.slice(0, template.assetPath.lastIndexOf('/'))
    : '';
  const effectiveFolder = folder || templateFolder || cat?.newAssetFolder || 'Assets/Data';
  const effectiveName = assetName || toAssetName(key);
  const path = `${effectiveFolder.replace(/\/+$/, '')}/${effectiveName}.asset`;

  const create = async () => {
    if (!template) return;
    setBusy(true);
    try {
      const values = { ...(template.unityValues ?? template.values ?? {}) };
      if (keyField && key.trim()) values[keyField] = key.trim();
      if (cat?.nameField && cat.nameField !== keyField && key.trim()) values[cat.nameField] = key.trim();
      const rec = await pb.collection('data_records').create<DataRecord>({
        category,
        template: template.guid,
        assetName: effectiveName,
        pendingPath: path,
        values,
      });
      setData({ records: { ...getData().records, [rec.id]: rec }, selectedId: rec.id, category });
      toast(t('data.create.done'));
      onClose();
    } catch (err) {
      toast(errorMessage(err), 'error');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="modal-back" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal card" role="dialog" aria-label={t('data.create.title')}>
        <h3>{t('data.create.title')}</h3>
        <p className="muted small">{t('data.create.help')}</p>
        <label>
          {t('data.create.template')}
          <select value={templateId} onChange={(e) => setTemplateId(e.target.value)}>
            {templates.map((r) => (
              <option key={r.id} value={r.id}>
                {r.name}
              </option>
            ))}
          </select>
        </label>
        {template && (
          <div className="row small muted">
            <DataIcon iconKey={template.icon} size={24} />
            <span className="mono">{template.assetPath}</span>
          </div>
        )}
        {keyField && (
          <label>
            {t('data.create.key', { field: keyField })}
            <input autoFocus value={key} onChange={(e) => setKey(e.target.value)} />
          </label>
        )}
        <label>
          {t('data.create.assetName')}
          <input
            value={assetName}
            placeholder={toAssetName(key)}
            onChange={(e) => setAssetName(e.target.value)}
          />
        </label>
        <label>
          {t('data.create.folder')}
          <input value={folder} placeholder={effectiveFolder} onChange={(e) => setFolder(e.target.value)} />
        </label>
        <p className="small">
          {t('data.create.willBe')} <span className="mono">{path}</span>
        </p>
        <div className="row">
          <div className="spacer" />
          <button onClick={onClose}>{t('common.cancel')}</button>
          <button
            className="primary"
            disabled={busy || !template || !effectiveName || (!!keyField && !key.trim())}
            onClick={() => void create()}
          >
            {t('data.create.create')}
          </button>
        </div>
      </div>
    </div>
  );
}

/** "Wolf Paw" → "Wolf_Paw": the file-name convention of Assets/Data (letters, digits, _). */
export function toAssetName(s: string): string {
  return s
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^A-Za-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 80);
}
